<script lang="ts">
  import type { Snippet } from 'svelte';
  import { cn } from '../../lib/utils/cn';

  type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive';
  type Size = 'md' | 'lg';

  interface Props {
    variant?: Variant;
    size?: Size;
    disabled?: boolean;
    fullWidth?: boolean;
    class?: string;
    type?: 'button' | 'submit' | 'reset';
    onclick?: (event: MouseEvent) => void;
    ariaLabel?: string;
    ariaPressed?: boolean;
    children?: Snippet;
    'data-testid'?: string;
  }

  let {
    variant = 'primary',
    size = 'md',
    disabled = false,
    fullWidth = false,
    class: className = '',
    type = 'button',
    onclick,
    ariaLabel,
    ariaPressed,
    children,
    'data-testid': dataTestId,
  }: Props = $props();

  const variantClasses: Record<Variant, string> = {
    primary:
      'bg-tx42-felt-button text-white border-transparent hover:bg-[var(--tx42-felt-button-hover)] active:bg-[var(--tx42-felt-button-active)]',
    secondary:
      'bg-tx42-panel text-tx42-text border-[var(--tx42-panel-border-subtle)] hover:bg-tx42-panel-elevated',
    ghost:
      'bg-transparent text-tx42-text border-transparent hover:bg-[var(--tx42-team-us-muted)]',
    destructive:
      'bg-tx42-destructive text-white border-transparent hover:opacity-90 active:opacity-80',
  };

  const sizeClasses: Record<Size, string> = {
    md: 'min-h-touch px-4 py-2 text-sm font-semibold rounded-tx42',
    lg: 'min-h-touch-primary px-6 py-3 text-base font-semibold rounded-[var(--tx42-radius-xl)]',
  };
</script>

<button
  type={type}
  class={cn(
    'tx42-interactive inline-flex items-center justify-center gap-2 border font-sans',
    variantClasses[variant],
    sizeClasses[size],
    fullWidth && 'w-full',
    className,
  )}
  disabled={disabled}
  onclick={onclick}
  aria-label={ariaLabel}
  {...ariaPressed !== undefined ? { 'aria-pressed': ariaPressed } : {}}
  data-testid={dataTestId}
>
  {#if children}
    {@render children()}
  {/if}
</button>
